import { SUBSCRIPTION_EXP_ENABLED_HEADER } from '@src/constants/api.headers.constants'
import { FEATURE_HEADERS } from '@src/quince-site-common/src/utils/constants'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b'
import { createBatches } from '@src/utils/general.utils'

import Product, { IProduct } from '../../domains/Product/Product'
import QuinceApiError from '../../domains/quince-api-error'
import filterFalsy from '../../utils/filterFalsy'
import { getContentEnvMode, getContentProductsEnv } from '../../utils/getEnvironmentParam'
import { sandServicesHttpClient, presentationServiceClient } from '../../utils/HttpClient'
import { B2B_URL_PREFIX } from '../cartCheckoutService/constants'

import {
  GetProductRequest,
  GetProductRequestWithSpids,
  GetProductRequestWithContentfulProductIds,
  GetProductRequestWithInternalProductIds,
  GetPLRefreshProductRequest,
} from './productService.types'

/**
 * API Configuration Rules:
 * - useLite=false: Executes full enrichment based on provided options
 * - useLite=true: Executes lite enrichment, ignoring provided options
 * - options=ENRICH_REFRESH_LITE: Forces lite enrichment regardless of useLite flag. Other options passed will also be enriched if useLite is false.
 * - options=ENRICH_ONLY_TAGS: Restricts enrichment to tags only. Value of useLite doesnt matter. Other Options passed are ignored.
 */
export async function fetchAndEnrichProducts(
  fetchProductRequest?: GetProductRequest,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const {
    spids = undefined,
    internalProductIds = undefined,
    contentfulProductIds,
    options = undefined,
    useLite = false,
    zipCode = undefined,
    extraParams = undefined,
  } = fetchProductRequest ?? {}
  let productList: IProduct[] = []
  if ((spids?.length ?? 0) == 0 && (contentfulProductIds?.length ?? 0) == 0 && (internalProductIds?.length ?? 0) == 0) {
    return []
  }

  try {
    const apiUrlToAppend = getStoreProgramIdFromStorage() ? `/${B2B_URL_PREFIX}` : ''
    const { data: products } = await sandServicesHttpClient.get<IProduct[]>(`${apiUrlToAppend}/products/enrich`, {
      params: {
        ...extraParams,
        productIds: spids?.join(','),
        internalProductIds: internalProductIds?.join(','),
        contentfulProductIds: contentfulProductIds?.join(','),
        options: options?.join(','),
        zipCode: zipCode,
        environment: getContentProductsEnv(),
        useLite,
      },
      headers: {
        [SUBSCRIPTION_EXP_ENABLED_HEADER]: subscriptionExpEnabled ? 'true' : 'false',
      },
    })
    productList = products
  } catch (e) {
    throw new QuinceApiError({
      error: e as Error,
      message: 'Failed while fetching products enrich getProducts() runtime',
      apiName: 'PRODUCTS_ENRICH_FETCH',
      data: {
        fetchProductRequest,
      },
    })
  }

  return filterFalsy(productList).map(x => new Product(x))
}

export async function fetchPLRefreshProducts(
  fetchProductRequest?: GetPLRefreshProductRequest,
  extraHeaders?: Record<string, string>,
): Promise<Product | undefined> {
  const { internalProductId = undefined, options = undefined } = fetchProductRequest ?? {}
  let product: IProduct | undefined = undefined
  if (!internalProductId) {
    return undefined
  }

  try {
    const { data } = await presentationServiceClient.post<null, IProduct>('/refresh', null, {
      params: {
        productId: internalProductId,
        options: options?.join(','),
        environment: getContentProductsEnv(),
      },
      headers: {
        ...(extraHeaders ?? {}),
        [FEATURE_HEADERS.CONTENT_ENV_MODE]: getContentEnvMode(),
      },
    })
    product = data
  } catch (e) {
    throw new QuinceApiError({
      error: e as Error,
      message: 'Failed while fetching PL refresh product fetchPLRefreshProducts() runtime',
      apiName: 'PL_REFRESH_PRODUCT_FETCH',
      data: {
        fetchProductRequest,
      },
    })
  }

  return product ? new Product(product) : undefined
}

export async function fetchAndEnrichProductsBySpidsUsingBatching(
  fetchProductRequest: GetProductRequestWithSpids,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.spids)
  const products = await Promise.all(
    batches.map(batch => fetchAndEnrichProducts({ ...fetchProductRequest, spids: batch }, subscriptionExpEnabled)),
  )
  return products.flat()
}

export async function fetchAndEnrichProductsByContentfulIdsUsingBatching(
  fetchProductRequest: GetProductRequestWithContentfulProductIds,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.contentfulProductIds)
  const products = await Promise.all(
    batches.map(batch =>
      fetchAndEnrichProducts({ ...fetchProductRequest, contentfulProductIds: batch }, subscriptionExpEnabled),
    ),
  )
  return products.flat()
}

export async function fetchAndEnrichProductsByInternalProductIdsUsingBatching(
  fetchProductRequest: GetProductRequestWithInternalProductIds,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.internalProductIds)
  const products = await Promise.all(
    batches.map(batch =>
      fetchAndEnrichProducts({ ...fetchProductRequest, internalProductIds: batch }, subscriptionExpEnabled),
    ),
  )
  return products.flat()
}
